/* oxlint-disable no-underscore-dangle */
/**
 * Configuration resolved at RUNTIME (build-once → promote dev→staging→prod strategy).
 *
 * - Web/PWA: the container injects `window.__APP_CONFIG__` via `/config.js` (generated by
 *   the nginx entrypoint from environment variables at startup). A single image
 *   serves all environments; only the container env changes.
 * - Tauri (desktop/mobile): there is no `/config.js`; it falls back to the baked-in values from
 *   `import.meta.env.VITE_*` (build-time) → behavior unchanged.
 * - Local dev: same (import.meta.env from apps/web/.env) or fallback to localhost.
 */
interface AppRuntimeConfig {
  SERVER_URL?: string;
  POWERSYNC_URL?: string;
  WEB_URL?: string;
}

const runtime: AppRuntimeConfig =
  (globalThis as typeof globalThis & { __APP_CONFIG__?: AppRuntimeConfig }).__APP_CONFIG__ ?? {};

/**
 * First value ACTUALLY present. A bare `??` is not enough: the entrypoint
 * writes `"KEY": ""` for every env the container does not define, and an empty
 * string is not nullish → it would win over the default and leave, for example, a base
 * URL empty. Here, "unconfigured" and "empty" mean the same thing.
 */
function firstConfigured(...values: (string | undefined)[]): string | undefined {
  return values.find((v) => v !== undefined && v.trim() !== "")?.trim();
}

/** Backend base (auth/api/server). */
export const SERVER_URL: string =
  firstConfigured(
    runtime.SERVER_URL,
    import.meta.env.VITE_SERVER_URL,
    import.meta.env.VITE_API_URL,
  ) ?? "http://localhost:3333";

/** PowerSync service endpoint. */
export const POWERSYNC_URL: string =
  firstConfigured(runtime.POWERSYNC_URL, import.meta.env.VITE_POWERSYNC_URL) ??
  "http://localhost:8085";

/**
 * Public web base (`https://app…`). Only the native binary needs it:
 * inside Tauri the origin is `tauri://localhost`, so any link that
 * must open in the system browser —today the legal documents that
 * stores require to be accessible from inside the app— needs an absolute URL
 * that exists outside the binary.
 *
 * On the web it resolves to its own origin, so no environment (dev, rc,
 * prod, Pages preview, `localhost`) depends on someone configuring it.
 */
export const PUBLIC_WEB_URL_FALLBACK = "https://app.listtoapp.com";

/**
 * Origin usable OUTSIDE the WebView. Inside the native binary,
 * `location.origin` is `tauri://localhost` (or `http://tauri.localhost`): an
 * origin the system browser cannot open, so there it is not valid and
 * it falls back to the public host.
 */
function externallyUsableOrigin(): string {
  const origin = globalThis.location?.origin;
  if (!origin || origin.startsWith("tauri:") || origin.includes("tauri.localhost")) {
    return PUBLIC_WEB_URL_FALLBACK;
  }
  return origin;
}

export const WEB_URL: string =
  firstConfigured(runtime.WEB_URL, import.meta.env.VITE_WEB_URL) ?? externallyUsableOrigin();
